/**
 * Temporary debug surface for real-data wiring on the Accounts page.
 *
 * Two probes:
 *  1. `/api/broker-accounts` — always fetched when kind === "real",
 *     shows the raw response that powers the selector. This is the
 *     authoritative "the proxy works" signal.
 *  2. Freeform path probe — a text input backed by state. Paste any
 *     path from the real app's DevTools Network tab (e.g.
 *     `/broker-accounts/<id>/balance`, `/portfolio/<id>`) and hit Go to
 *     see the response. Used to discover endpoints for balances,
 *     curves, and metrics that `/broker-accounts` doesn't return.
 *
 * Deliberately dev-facing UI — this panel goes away once each real
 * endpoint feeds its intended surface.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";

import { useAuthStore } from "@/store/auth-store";
import { useRealBrokerAccounts } from "@/lib/whb-data";
import { whbFetch } from "@/lib/auth";

interface ProbeResult {
  status: "idle" | "loading" | "ok" | "error";
  data: unknown;
  error: string | null;
  durationMs: number | null;
  httpStatus: number | null;
}

const EMPTY_PROBE: ProbeResult = {
  status: "idle",
  data: null,
  error: null,
  durationMs: null,
  httpStatus: null,
};

/** Normalize whatever the user typed into a path we can hand to the
 *  proxy. `/api/foo` and `foo` both resolve to `/api/foo`. */
function normalize(path: string): string {
  const trimmed = path.trim().replace(/^\/+/, "");
  if (!trimmed) return "";
  if (trimmed.startsWith("api/")) return `/${trimmed}`;
  return `/api/${trimmed}`;
}

export function RealBrokerAccountsDebugPanel() {
  const kind = useAuthStore((s) => s.kind);
  const { data, error, loading, durationMs } = useRealBrokerAccounts();
  const [open, setOpen] = useState(true);

  // Path probe state. Seeded with a plausible next-step — pick any
  // account id from the /broker-accounts response and try the detail
  // endpoint (we don't know the path yet; Sergey enters it).
  const [probePath, setProbePath] = useState("/broker-accounts/");
  const [probe, setProbe] = useState<ProbeResult>(EMPTY_PROBE);

  // "copied" slot tracks which copy button just fired so the icon can
  // flip to a checkmark for ~1.2s. Single slot because only one copy
  // can be the "most recent" at a time.
  const [copied, setCopied] = useState<null | "json" | "ids" | "probe">(null);

  /**
   * Cross-browser clipboard write. `navigator.clipboard.writeText`
   * works on iOS Safari + Android Chrome over HTTPS, but a
   * textarea+execCommand fallback keeps this robust on older WebViews.
   */
  async function writeClipboard(text: string): Promise<boolean> {
    if (!text) return false;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to legacy path
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  function flashCopied(which: "json" | "ids" | "probe") {
    setCopied(which);
    window.setTimeout(() => {
      setCopied((c) => (c === which ? null : c));
    }, 1200);
  }

  if (kind !== "real") return null;

  const status: "loading" | "error" | "ok" = loading
    ? "loading"
    : error
    ? "error"
    : "ok";
  const color: "info" | "error" | "success" =
    status === "loading" ? "info" : status === "error" ? "error" : "success";

  const prettyBody =
    data === null ? "" : (() => {
      try {
        return JSON.stringify(data, null, 2);
      } catch {
        return String(data);
      }
    })();
  const previewCount = Array.isArray(data) ? data.length : null;

  async function runProbe() {
    const normalized = normalize(probePath);
    if (!normalized) {
      setProbe({
        ...EMPTY_PROBE,
        status: "error",
        error: "Enter a path first.",
      });
      return;
    }
    setProbe({
      ...EMPTY_PROBE,
      status: "loading",
    });
    const start = performance.now();
    try {
      const body = await whbFetch<unknown>(normalized);
      setProbe({
        status: "ok",
        data: body,
        error: null,
        durationMs: Math.round(performance.now() - start),
        httpStatus: 200,
      });
    } catch (err) {
      const msg = (err as Error).message ?? String(err);
      const match = msg.match(/request failed (\d+)/);
      setProbe({
        status: "error",
        data: null,
        error: msg,
        durationMs: Math.round(performance.now() - start),
        httpStatus: match ? Number(match[1]) : null,
      });
    }
  }

  const probePretty = (() => {
    if (probe.data === null || probe.data === undefined) return "";
    try {
      return JSON.stringify(probe.data, null, 2);
    } catch {
      return String(probe.data);
    }
  })();

  async function copyJson() {
    if (!prettyBody) return;
    const ok = await writeClipboard(prettyBody);
    if (ok) flashCopied("json");
  }

  async function copyIds() {
    if (!Array.isArray(data)) return;
    const ids = data
      .map((row) => (row && typeof row === "object" ? (row as { id?: string }).id : undefined))
      .filter((id): id is string => typeof id === "string")
      .join("\n");
    if (!ids) return;
    const ok = await writeClipboard(ids);
    if (ok) flashCopied("ids");
  }

  async function copyProbe() {
    if (!probePretty) return;
    const ok = await writeClipboard(probePretty);
    if (ok) flashCopied("probe");
  }

  return (
    <Box sx={{ mt: 1.5, mb: 2 }}>
      <Alert
        severity={color}
        icon={<ScienceRounded fontSize="small" />}
        variant="outlined"
        sx={{ alignItems: "center", borderRadius: 2 }}
        action={
          <Button
            size="small"
            endIcon={open ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide" : "Show"}
          </Button>
        }
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" fontWeight={700}>
            /api/broker-accounts
          </Typography>
          {status === "loading" && (
            <Chip size="small" label="Loading…" variant="outlined" />
          )}
          {status === "error" && (
            <Chip size="small" color="error" label="Error" />
          )}
          {status === "ok" && (
            <Chip
              size="small"
              color="success"
              label={
                previewCount !== null
                  ? `OK · ${previewCount} item${previewCount === 1 ? "" : "s"}`
                  : "OK"
              }
            />
          )}
          {durationMs !== null && (
            <Typography variant="caption" color="text.secondary">
              {durationMs} ms
            </Typography>
          )}
          {status === "ok" && prettyBody.length > 0 && (
            <Tooltip title="Copy full JSON response">
              <Button
                size="small"
                variant="outlined"
                color={copied === "json" ? "success" : "primary"}
                startIcon={
                  copied === "json" ? (
                    <CheckRounded sx={{ fontSize: 16 }} />
                  ) : (
                    <ContentCopyRounded sx={{ fontSize: 16 }} />
                  )
                }
                onClick={copyJson}
                sx={{ minHeight: 28, py: 0.25, px: 1 }}
              >
                {copied === "json" ? "Copied" : "Copy JSON"}
              </Button>
            </Tooltip>
          )}
          {Array.isArray(data) && previewCount && previewCount > 0 && (
            <Tooltip title="Copy all ids (newline separated)">
              <IconButton size="small" onClick={copyIds}>
                {copied === "ids" ? (
                  <CheckRounded sx={{ fontSize: 16 }} color="success" />
                ) : (
                  <ContentCopyRounded sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Alert>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1.5,
            maxHeight: 260,
            overflow: "auto",
            fontFamily:
              '"JetBrains Mono","SFMono-Regular",Menlo,Consolas,monospace',
            fontSize: "0.75rem",
            lineHeight: 1.4,
            bgcolor: "background.paper",
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "text.primary",
          }}
        >
          {status === "loading" && "Fetching /api/broker-accounts…"}
          {status === "error" && (error ?? "Unknown error")}
          {status === "ok" &&
            (prettyBody.length > 0
              ? prettyBody
              : "(empty response body)")}
        </Box>

        {/* Freeform probe — paste any path from the WHB app's Network
         *  tab and hit Go to see what comes back. Use this to find the
         *  balances + curves endpoints `/broker-accounts` doesn't
         *  return. */}
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            border: (t) => `1px dashed ${t.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            Probe another endpoint. Example: <code>/broker-accounts/&lt;id&gt;</code>,
            <code>/portfolio/&lt;id&gt;</code>,{" "}
            <code>/bots</code>. Paths resolve against <code>/api</code>.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              value={probePath}
              onChange={(e) => setProbePath(e.target.value)}
              placeholder="/broker-accounts/<id>"
              onKeyDown={(e) => {
                if (e.key === "Enter") void runProbe();
              }}
              InputProps={{
                sx: {
                  fontFamily:
                    '"JetBrains Mono","SFMono-Regular",Menlo,Consolas,monospace',
                  fontSize: "0.8rem",
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowRounded />}
              onClick={runProbe}
              disabled={probe.status === "loading"}
            >
              Go
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
            {probe.status === "loading" && (
              <Chip size="small" label="Running…" variant="outlined" />
            )}
            {probe.status === "ok" && (
              <Chip
                size="small"
                color="success"
                label={
                  Array.isArray(probe.data)
                    ? `OK · ${probe.data.length} item${probe.data.length === 1 ? "" : "s"}`
                    : "OK"
                }
              />
            )}
            {probe.status === "error" && (
              <Chip
                size="small"
                color="error"
                label={
                  probe.httpStatus
                    ? `HTTP ${probe.httpStatus}`
                    : "Error"
                }
              />
            )}
            {probe.durationMs !== null && (
              <Typography variant="caption" color="text.secondary">
                {probe.durationMs} ms
              </Typography>
            )}
            {probe.status !== "idle" && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {normalize(probePath)}
              </Typography>
            )}
            {probe.status === "ok" && probePretty.length > 0 && (
              <Tooltip title="Copy probe response JSON">
                <IconButton size="small" onClick={copyProbe} sx={{ ml: "auto" }}>
                  {copied === "probe" ? (
                    <CheckRounded sx={{ fontSize: 16 }} color="success" />
                  ) : (
                    <ContentCopyRounded sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {probe.status !== "idle" && (
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1.25,
                maxHeight: 260,
                overflow: "auto",
                fontFamily:
                  '"JetBrains Mono","SFMono-Regular",Menlo,Consolas,monospace',
                fontSize: "0.72rem",
                lineHeight: 1.4,
                bgcolor: "background.paper",
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 2,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "text.primary",
              }}
            >
              {probe.status === "loading" && "Running…"}
              {probe.status === "error" && (probe.error ?? "Unknown error")}
              {probe.status === "ok" &&
                (probePretty.length > 0 ? probePretty : "(empty response body)")}
            </Box>
          )}
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.75 }}
        >
          Debug-only. Selector below now uses real accounts; balances
          still read from mock until a per-account endpoint is wired.
        </Typography>
      </Collapse>
    </Box>
  );
}
